// "Find leads for me" (modelled on Explee): the AI reads our website and won deals, says what we sell and proposes
// buyer segments with a brief (problem, role, criteria, example clients) that the user reviews; Search then runs the
// chosen segments on every engine that has a key, drops non-buyers and leads the user rejected before, scores each
// result against the segment's criteria, reads the strongest candidates' websites and scores them again, and drafts a
// first email per lead on request. "Not a fit" (with a reason) teaches the next runs. Nothing is saved or sent on its own: rows are ticked and added as leads, and an
// email is copied, opened in the mail app, or sent from the user's Outlook with one click (outlook.ts).
import { useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  Copy,
  Search,
  ThumbsDown,
  Loader2,
  Mail,
  PenLine,
  Send,
  Sparkles,
  UserPlus,
} from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import type { CrmDataProvider } from "../../providers/types";
import {
  useConfigurationContext,
  useConfigurationUpdater,
} from "../../root/ConfigurationContext";
import type { Company, Deal } from "../../types";
import { runAi, runAiJson } from "../ai";
import { lookupCompany, recentNews, recentSignals } from "../companyLookup";
import {
  brochuresOf,
  buildDraftPrompt,
  buildSegmentsPrompt,
  DEFAULT_OUR_WEBSITE,
  addRejection,
  DraftSchema,
  engineDone,
  engineRefused,
  gatherEvidence,
  isRejected,
  mergeRanked,
  nextEngine,
  notABuyer,
  rankedRows,
  rankLeads,
  searchFilters,
  RankSchema,
  segmentEngines,
  SegmentsSchema,
  writeDraft,
  type Brochure,
  type Draft,
  type RankedLead,
  type Rejection,
  type SegmentsAnswer,
} from "../../../../../supabase/functions/_shared/leadAgent.ts";
import {
  isOpenEngine,
  type OpenEngine,
} from "../../../../../supabase/functions/_shared/openData.ts";
import {
  searchedPlan,
  unpausedIndexes,
  weightOf,
} from "../../../../../supabase/functions/_shared/segmentResults.ts";
import {
  ENGINE_KEY,
  type KeyEngine,
  type Lead,
  type LeadEngine,
} from "../../../../../supabase/functions/_shared/leadFilters.ts";
import {
  addLeads,
  agentFit,
  profileText,
  runLeadSearch,
  useEngineStatus,
} from "./leads";
import {
  DEFAULT_SEND_AS,
  sendLeadEmail,
  senderFor,
  withLinks,
  type Sender,
} from "./outlook";
import { draftNoteText } from "./draftNote";
import { SegmentReview } from "./SegmentReview";
import { SenderSelect } from "./SenderSelect";
import { useDefaultSender } from "./useDefaultSender";

const MAX_CUSTOMERS = 30;

// An engine whose plan refuses API searches (Apollo's free plan) is skipped for a day instead of costing a look-up
// in every run. ponytail: per browser (localStorage); a paid plan is noticed within a day.
const REFUSED_FOR_MS = 24 * 60 * 60 * 1000;
const refusedKey = (engine: KeyEngine) => `ithnk.refused.${engine}`;
const recentlyRefused = (engine: KeyEngine) => {
  try {
    return (
      Date.now() - Number(localStorage.getItem(refusedKey(engine)) ?? 0) <
      REFUSED_FOR_MS
    );
  } catch {
    return false;
  }
};
const rememberRefused = (engine: KeyEngine) => {
  try {
    localStorage.setItem(refusedKey(engine), String(Date.now()));
  } catch {
    /* private mode: refused again next run */
  }
};
/** Candidates per segment whose website and recent news are read before scoring */
const EVIDENCE_PER_SEGMENT = 30;

/**
 * With `onAdd` (the Generate page), the ticked rows go to it instead of becoming deals, and "Not a fit" only drops the
 * row (the page's Remove teaches the next runs); `rejections` then replaces the Settings list.
 */
export const LeadAgent = ({
  onAdd,
  rejections: rejectionsProp,
}: {
  onAdd?: (rows: RankedLead[], plan: SegmentsAnswer) => Promise<void>;
  rejections?: Rejection[];
} = {}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const config = useConfigurationContext();
  const { data: status = [] } = useEngineStatus();
  const website = config.our_website || DEFAULT_OUR_WEBSITE;
  const brochures: Brochure[] = config.lead_brochures ?? [];

  const [running, setRunning] = useState(false);
  const [focus, setFocus] = useState("");
  const [log, setLog] = useState<string[]>([]);
  const [plan, setPlan] = useState<SegmentsAnswer | null>(null);
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const setConfig = useConfigurationUpdater();
  const rejections: Rejection[] =
    rejectionsProp ?? config.lead_rejections ?? [];
  const [rows, setRows] = useState<RankedLead[]>([]);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [drafting, setDrafting] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const say = (line: string) => setLog((lines) => [...lines, line]);
  // a failure is shown and stays in the run log (a toast alone is gone before anyone reads it)
  const fail = (error: unknown) => {
    const message = (error as Error).message;
    notify(message, { type: "error" });
    say(message);
  };

  const wonCustomers = async () => {
    const { data: deals } = await dataProvider.getList<Deal>("deals", {
      filter: { stage: "won" },
      pagination: { page: 1, perPage: MAX_CUSTOMERS },
      sort: { field: "updated_at", order: "DESC" },
    });
    const ids = [...new Set(deals.map((d) => d.company_id).filter(Boolean))];
    if (!ids.length) return [];
    const { data: companies } = await dataProvider.getMany<Company>(
      "companies",
      { ids },
    );
    return companies.map((c) => ({
      name: c.name,
      sector: c.sector,
      size: c.size,
      country: c.country,
    }));
  };

  /** Step 1: what we sell and the segments with their briefs, for review */
  const run = async () => {
    setRunning(true);
    setLog([]);
    setPlan(null);
    setRows([]);
    setTicked(new Set());
    setDrafts({});
    try {
      say(translate("resources.leads.agent.reading", { website }));
      const site = await lookupCompany(website);
      const customers = await wonCustomers();
      say(
        translate("resources.leads.agent.customers", {
          smart_count: customers.length,
        }),
      );
      const profile = config.ideal_customers?.[0];
      const answer = await runAiJson(
        "lead_segments",
        buildSegmentsPrompt({
          website,
          site: site.pages,
          customers,
          profile: profile ? profileText(profile) : undefined,
          focus: focus.trim() || undefined,
          rejections,
        }),
        SegmentsSchema,
      );
      setPlan(answer);
      // a segment the User paused (Segment results) starts unticked
      setChosen(unpausedIndexes(answer.segments, config.lead_night_plan));
      say(translate("resources.leads.agent.review"));
    } catch (error) {
      fail(error);
    } finally {
      setRunning(false);
    }
  };

  /** Step 2: the chosen segments searched, filtered, scored, checked on their websites and scored again */
  const search = async () => {
    if (!plan) return;
    setRunning(true);
    setRows([]);
    setTicked(new Set());
    setDrafts({});
    try {
      const keyed = new Set<KeyEngine>(
        status
          .filter((s) => s.has_key && !recentlyRefused(s.engine))
          .map((s) => s.engine),
      );
      const segments = plan.segments.filter((_, i) => chosen.has(i));
      // the brief searched last is the one the nightly run searches with; it never changes it. Only an admin may
      // save the configuration, so a sales user's search leaves the saved brief as it is.
      dataProvider
        .updateConfiguration({
          ...config,
          lead_night_plan: searchedPlan(
            config.lead_night_plan,
            plan.offer,
            segments,
          ),
        })
        .then(setConfig, () => undefined);
      const found: RankedLead[] = [];
      for (const segment of segments) {
        const engines = segmentEngines(segment, keyed);
        if (!engines.length)
          say(
            translate("resources.leads.agent.no_engine", {
              segment: segment.name,
            }),
          );
        const tried: (LeadEngine | OpenEngine)[] = [];
        let headOffices = 0;
        // a segment the User scaled searches more pages of each keyed engine
        const pages = weightOf(config.lead_night_plan, segment.name);
        for (
          let engine = nextEngine(segment, keyed, tried, headOffices, pages);
          engine;
          engine = nextEngine(segment, keyed, tried, headOffices, pages)
        ) {
          const page = tried.filter((e) => e === engine).length + 1;
          tried.push(engine);
          say(
            translate("resources.leads.agent.searching", {
              segment: segment.name,
              engine: translate(`resources.leads.engines.${engine}`),
            }),
          );
          let leads: Lead[];
          try {
            leads =
              (
                await runLeadSearch(
                  dataProvider,
                  engine,
                  searchFilters(segment, engine),
                  page,
                  translate("resources.leads.still_running"),
                )
              ).results ?? [];
          } catch (error) {
            // one search failing (the daily cap, a timeout) does not stop the run; an engine whose plan or key
            // refuses API searches is dropped for the rest of the run, and the segment tries the next engine
            const message = (error as Error).message;
            say(message);
            engineDone(tried, engine, pages);
            if (engineRefused(message) && !isOpenEngine(engine)) {
              keyed.delete(ENGINE_KEY[engine]);
              rememberRefused(ENGINE_KEY[engine]);
            }
            continue;
          }
          const buyers = leads.filter(
            (lead) => !notABuyer(lead) && !isRejected(lead, rejections),
          );
          if (buyers.length < leads.length)
            say(
              translate("resources.leads.agent.not_buyers", {
                smart_count: leads.length - buyers.length,
              }),
            );
          if (!buyers.length) continue;
          const candidates = buyers.slice(0, EVIDENCE_PER_SEGMENT);
          say(
            translate("resources.leads.agent.reading_sites", {
              smart_count: candidates.length,
              segment: segment.name,
            }),
          );
          const evidence = await gatherEvidence(candidates, {
            site: lookupCompany,
            news: recentNews,
            signals: recentSignals,
          });
          // the fit comes from the criteria scores (fitOf)
          const ranked = await rankLeads(
            (prompt) => runAiJson("rank_leads", prompt, RankSchema),
            plan.offer,
            segment,
            candidates,
            { ...evidence, rejections },
            (error) =>
              // the leads stay, unscored; the reason shows in the run log
              say(
                translate("resources.leads.agent.rank_failed", {
                  error: error.message,
                }),
              ),
          );
          if (isOpenEngine(engine)) headOffices += candidates.length;
          found.push(...rankedRows(segment, candidates, ranked, evidence));
          setRows(mergeRanked(found));
        }
      }
      say(
        translate("resources.leads.agent.done", {
          smart_count: mergeRanked(found).length,
        }),
      );
    } catch (error) {
      fail(error);
    } finally {
      setRunning(false);
      queryClient.invalidateQueries({ queryKey: ["lead_engines", "usage"] });
    }
  };

  /** "Not a fit": the lead leaves the list and, with the reason, teaches the next runs */
  const reject = async (row: RankedLead, reason: string) => {
    if (onAdd) {
      setRows((all) => all.filter((r) => r.lead.ref !== row.lead.ref));
      return;
    }
    const next = {
      ...config,
      lead_rejections: addRejection(rejections, {
        name: row.lead.company.name,
        domain: row.lead.company.domain,
        industry: row.lead.company.industry,
        segment: row.segment,
        reason: reason.trim() || undefined,
      }),
    };
    try {
      setConfig(await dataProvider.updateConfiguration(next));
      setRows((all) => all.filter((r) => r.lead.ref !== row.lead.ref));
      notify("resources.leads.agent.rejected", { type: "info" });
    } catch (error) {
      fail(error);
    }
  };

  const draft = async (row: RankedLead) => {
    setDrafting(row.lead.ref);
    try {
      const site = row.lead.company.website
        ? await lookupCompany(row.lead.company.website).catch(() => null)
        : null;
      const { draft: answer } = await writeDraft(
        (prompt, answered) => runAi("draft_email", prompt, answered),
        buildDraftPrompt({
          sender: identity?.fullName || config.title || "THNK",
          company: config.title || "THNK",
          offer: plan?.offer ?? "",
          lead: row,
          leadSite: site?.pages,
          brochures,
        }),
        DraftSchema,
      );
      setDrafts((all) => ({ ...all, [row.lead.ref]: answer }));
    } catch (error) {
      fail(error);
    } finally {
      setDrafting(null);
    }
  };

  const add = async () => {
    const chosen = rows.filter((r) => ticked.has(r.lead.ref));
    setAdding(true);
    try {
      if (onAdd) {
        if (plan) await onAdd(chosen, plan);
        setRows(rows.filter((r) => !ticked.has(r.lead.ref)));
        setTicked(new Set());
        return;
      }
      const { added, skipped, deals } = await addLeads(
        chosen.map((r) => r.lead),
        dataProvider,
        {
          salesId: identity?.id,
          sectors: config.companySectors,
          fits: Object.fromEntries(
            chosen.map((r) => [
              r.lead.ref,
              agentFit(r.fit, r.reason, r.segment),
            ]),
          ),
        },
      );
      // a drafted email goes with its lead, as the deal's DRAFT EMAIL note
      for (const row of chosen) {
        const draft = drafts[row.lead.ref];
        const dealId = deals[row.lead.ref];
        if (!draft || !dealId) continue;
        await dataProvider.create("deal_notes", {
          data: {
            deal_id: dealId,
            sales_id: identity?.id,
            date: new Date().toISOString(),
            text: draftNoteText({
              subject: draft.subject,
              body: draft.body,
              attach: brochuresOf(draft, brochures).map((b) => b.name),
              to: row.lead.contact?.email,
            }),
          },
        });
      }
      notify("resources.leads.added", {
        type: "success",
        messageArgs: { smart_count: added, skipped: skipped.length },
      });
      setRows(rows.filter((r) => !ticked.has(r.lead.ref)));
      setTicked(new Set());
    } catch (error) {
      fail(error);
    } finally {
      setAdding(false);
    }
  };

  const toggle = (ref: string) => {
    const next = new Set(ticked);
    if (next.has(ref)) next.delete(ref);
    else next.add(ref);
    setTicked(next);
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Bot className="size-6 text-muted-foreground" />
          <div className="flex-1 min-w-48">
            <h2 className="text-xl font-semibold">
              {translate("resources.leads.agent.title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {translate("resources.leads.agent.help", { website })}
            </p>
          </div>
          <Button type="button" disabled={running} onClick={run}>
            {running ? <Loader2 className="animate-spin" /> : <Sparkles />}
            {translate("resources.leads.agent.run")}
          </Button>
        </div>
        <Input
          aria-label={translate("resources.leads.agent.focus")}
          placeholder={translate("resources.leads.agent.focus_placeholder")}
          value={focus}
          disabled={running}
          onChange={(e) => setFocus(e.target.value)}
        />

        {log.length > 0 && (
          <ul
            className="text-sm text-muted-foreground list-disc pl-5"
            aria-live="polite"
          >
            {log.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        )}

        {plan && (
          <div className="flex flex-col gap-2">
            {plan.offer && (
              <p className="text-sm">
                <span className="font-medium">
                  {translate("resources.leads.agent.offer")}
                </span>{" "}
                {plan.offer}
              </p>
            )}
            <SegmentReview
              segments={plan.segments}
              chosen={chosen}
              disabled={running}
              onToggle={(i) => {
                const next = new Set(chosen);
                if (next.has(i)) next.delete(i);
                else next.add(i);
                setChosen(next);
              }}
              onEdit={(i, segment) =>
                setPlan({
                  ...plan,
                  segments: plan.segments.map((s, j) =>
                    j === i ? segment : s,
                  ),
                })
              }
            />
            <div className="flex justify-end">
              <Button
                type="button"
                disabled={running || !chosen.size}
                onClick={search}
              >
                {running ? <Loader2 className="animate-spin" /> : <Search />}
                {translate("resources.leads.agent.search", {
                  smart_count: chosen.size,
                })}
              </Button>
            </div>
          </div>
        )}

        {rows.length > 0 && (
          <>
            <div className="flex justify-end">
              <Button
                type="button"
                disabled={!ticked.size || adding || running}
                onClick={add}
              >
                {adding ? <Loader2 className="animate-spin" /> : <UserPlus />}
                {translate(
                  onAdd
                    ? "resources.pre_leads.add_found"
                    : "resources.leads.add",
                  { smart_count: ticked.size },
                )}
              </Button>
            </div>
            <ul className="flex flex-col divide-y">
              {rows.map((row) => (
                <AgentRow
                  key={row.lead.ref}
                  row={row}
                  ticked={ticked.has(row.lead.ref)}
                  onToggle={() => toggle(row.lead.ref)}
                  draft={drafts[row.lead.ref]}
                  drafting={drafting === row.lead.ref}
                  canDraft={drafting === null && !running}
                  onDraft={() => draft(row)}
                  onEdit={(next) =>
                    setDrafts((all) => ({ ...all, [row.lead.ref]: next }))
                  }
                  brochures={brochures}
                  onReject={(reason) => reject(row, reason)}
                />
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const AgentRow = ({
  row,
  ticked,
  onToggle,
  draft,
  drafting,
  canDraft,
  onDraft,
  onEdit,
  brochures,
  onReject,
}: {
  onReject: (reason: string) => void;
  row: RankedLead;
  ticked: boolean;
  onToggle: () => void;
  draft?: Draft;
  drafting: boolean;
  canDraft: boolean;
  onDraft: () => void;
  onEdit: (draft: Draft) => void;
  brochures: Brochure[];
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const config = useConfigurationContext();
  const sendAs = config.outlook_send_as || DEFAULT_SEND_AS;
  const { lead } = row;
  const [to, setTo] = useState(lead.contact?.email ?? "");
  const defaultSender = useDefaultSender();
  const [sender, setSender] = useState<Sender>(() =>
    senderFor(lead.contact?.title, defaultSender),
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [why, setWhy] = useState("");
  const chosen = draft ? brochuresOf(draft, brochures) : [];
  const fullBody = draft ? withLinks(draft.body, chosen) : "";
  const toggleBrochure = (brochure: Brochure) =>
    onEdit({
      ...draft!,
      brochure: null,
      brochures: chosen.includes(brochure)
        ? chosen.filter((b) => b !== brochure).map((b) => b.name)
        : [...chosen, brochure].map((b) => b.name),
    });
  const copy = async () => {
    await navigator.clipboard.writeText(`${draft!.subject}\n\n${fullBody}`);
    notify("resources.leads.agent.copied", { type: "info" });
  };
  const send = async () => {
    setSending(true);
    try {
      await sendLeadEmail({
        clientId: config.outlook_client_id!,
        sendAs,
        sender,
        to,
        subject: draft!.subject,
        body: draft!.body,
        brochures: chosen,
      });
      setSent(true);
      notify("resources.leads.agent.sent", {
        type: "info",
        messageArgs: { to: to.trim() },
      });
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    } finally {
      setSending(false);
    }
  };
  const mailto = draft
    ? `mailto:${encodeURIComponent(to.trim())}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(fullBody)}`
    : "";
  return (
    <li className="py-3 flex flex-col gap-2">
      <div className="flex gap-3 items-start">
        <Checkbox
          className="mt-1"
          aria-label={lead.company.name}
          checked={ticked}
          onCheckedChange={onToggle}
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{lead.company.name}</span>
            <Badge variant={row.fit >= 70 ? "default" : "secondary"}>
              {row.fit}%
            </Badge>
            <Badge variant="outline">{row.segment}</Badge>
          </div>
          <div className="text-sm text-muted-foreground">
            {[
              lead.contact &&
                `${lead.contact.first_name} ${lead.contact.last_name ?? ""}${lead.contact.title ? `, ${lead.contact.title}` : ""}`,
              lead.company.domain,
              lead.company.industry,
              [lead.company.city, lead.company.country]
                .filter(Boolean)
                .join(", "),
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
          <p className="text-sm">{row.reason}</p>
          {!!row.criteria?.length && (
            <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {row.criteria.map(({ criterion, score }) => (
                <li
                  key={criterion}
                  className={
                    score !== null && score >= 4 ? "text-foreground" : undefined
                  }
                >
                  {criterion}: {score === null ? "?" : `${score}/5`}
                </li>
              ))}
              {row.checked && (
                <li>{translate("resources.leads.agent.site_checked")}</li>
              )}
              {row.why_now && (
                <li className="text-foreground">
                  {translate("resources.leads.agent.why_now", {
                    fact: row.why_now,
                  })}
                </li>
              )}
            </ul>
          )}
          {rejecting && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Input
                className="max-w-sm"
                aria-label={translate("resources.leads.agent.not_fit_why")}
                placeholder={translate("resources.leads.agent.not_fit_why")}
                value={why}
                autoFocus
                onChange={(e) => setWhy(e.target.value)}
              />
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={() => onReject(why)}
              >
                {translate("resources.leads.agent.not_fit_confirm")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setRejecting(false)}
              >
                {translate("ra.action.cancel")}
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 items-end">
          {!draft && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canDraft}
              onClick={onDraft}
            >
              {drafting ? <Loader2 className="animate-spin" /> : <PenLine />}
              {translate("resources.leads.agent.draft")}
            </Button>
          )}
          {!rejecting && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setRejecting(true)}
            >
              <ThumbsDown />
              {translate("resources.leads.agent.not_fit")}
            </Button>
          )}
        </div>
      </div>
      {draft && (
        <div className="ml-7 flex flex-col gap-2">
          <Input
            type="email"
            aria-label={translate("resources.leads.agent.to")}
            placeholder={translate("resources.leads.agent.to")}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <Input
            aria-label={translate("resources.leads.agent.subject")}
            value={draft.subject}
            onChange={(e) => onEdit({ ...draft, subject: e.target.value })}
          />
          <Textarea
            aria-label={translate("resources.leads.agent.body")}
            className="min-h-40"
            value={draft.body}
            onChange={(e) => onEdit({ ...draft, body: e.target.value })}
          />
          {brochures.length > 0 && (
            <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <legend className="sr-only">
                {translate("resources.leads.agent.attach")}
              </legend>
              <span className="text-muted-foreground">
                {translate("resources.leads.agent.attach")}
              </span>
              {brochures.map((brochure) => (
                <label
                  key={brochure.url}
                  className="flex items-center gap-2 cursor-pointer min-h-8"
                >
                  <Checkbox
                    checked={chosen.includes(brochure)}
                    onCheckedChange={() => toggleBrochure(brochure)}
                  />
                  <a
                    href={brochure.url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline-offset-2 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {brochure.name}
                  </a>
                </label>
              ))}
            </fieldset>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={copy}>
              <Copy />
              {translate("resources.leads.agent.copy")}
            </Button>
            <Button type="button" variant="outline" size="sm" asChild>
              <a href={mailto}>
                <Mail />
                {translate("resources.leads.agent.open_mail")}
              </a>
            </Button>
            {config.outlook_client_id && (
              <Button
                type="button"
                size="sm"
                disabled={sending || sent || !/^\S+@\S+\.\S+$/.test(to.trim())}
                onClick={send}
              >
                {sending ? <Loader2 className="animate-spin" /> : <Send />}
                {translate("resources.leads.agent.send")}
              </Button>
            )}
          </div>
          {config.outlook_client_id && (
            <div className="flex flex-wrap items-center gap-3">
              <SenderSelect value={sender} onChange={setSender} />
              <span className="text-xs text-muted-foreground">
                {translate("resources.leads.agent.bcc", { address: sendAs })}
              </span>
            </div>
          )}
        </div>
      )}
    </li>
  );
};
